import React, { useEffect, useState } from 'react';
import {
  TypedUseSelectorHook,
  useSelector as useReduxSelector,
} from 'react-redux';
import { Navigate, useParams } from 'react-router';

import { faEdit } from '@fortawesome/pro-regular-svg-icons';

import { RootState } from '../../../../app/rootReducer';
import { IconLink } from '../../../../common/components/IconLink';
import { Decision, DecisionType } from '../../../../common/constants/decision';
import { ApplicationLifecycle } from '../../../../common/constants/lifecycle';
import { Resource } from '../../../../common/constants/resource';
import { isJuniorUnderwriter, NoraPermissionV2 } from '../../../../common/constants/roles';
import { tasksResolved } from '../../../../common/helpers';
import { useAccess } from '../../../../common/hooks/useAccess';
import { ApproveDrawer } from '../../../detail/drawer/approve/ApproveDrawer';
import { DeclineDrawer } from '../../../detail/drawer/decline/DeclineDrawer';
import { IneligibleDrawer } from '../../../detail/drawer/ineligible/IneligibleDrawer';
import { InvalidDrawer } from '../../../detail/drawer/InvalidDrawer';
import { PostponeDrawer } from '../../../detail/drawer/postpone/PostponeDrawer';
import { WaiveDrawer } from '../../../detail/drawer/waive/WaiveDrawer';
import { IDecision } from '../../../detail/interfaces/IDecision';
import { ITask } from '../../../tasks/interfaces/ITask';
import { getAllTasks } from '../../../tasks/slice/taskListSlice';
import * as styles from './styles.module.scss';
export const useSelector: TypedUseSelectorHook<RootState> = useReduxSelector;

const EditDecision = ({
  hasFinalDecision,
  hasPIADecision,
  isRUW,
}: {
  hasFinalDecision: boolean;
  hasPIADecision: boolean;
  isRUW: boolean;
}) => {
  const isJuniorUw = useAccess({
    permission: NoraPermissionV2.MAKE_PRELIMINARY_DECISION,
    legacyCheck: isJuniorUnderwriter,
  });

  const decisions: IDecision[] = useSelector(
    state => state.decision.decisions,
  ).filter(
    d => ![DecisionType.ENGINE, DecisionType.AF].includes(d.decisionType),
  );
  const isActivated = useSelector(state => state.application.activated);
  const lifecycle = useSelector(
    state => state.application.applicationLifecycleV2,
  );
  const tasks: ITask[] = useSelector(state => getAllTasks(state.taskList));
  const areTasksResolved = tasksResolved(tasks);

  const iconContainerClasses = `${styles.IconLinkContainer} ${styles.RightNavIconLinkContainer} ${styles.RightSidebar}`;

  // For controlling the ApproveDrawer component
  const [isOpen, setIsOpen] = useState(false);
  const [decisionToEdit, setDecisionToEdit] = useState(
    null as IDecision | null,
  );
  const [decision, setDecision] = useState(null as Decision | null);
  const [updateReason, setUpdateReason] = useState(null as string | null);

  const { id } = useParams<{ id: string }>();
  if (id === undefined) {
    return <Navigate to={{ pathname: '/' }} />;
  }

  const getLastDecision = (decisionsList: IDecision[]): IDecision => {
    let nonIgnoredDecisions = decisionsList.filter(d => !d.ignored);

    // for plan f, we want to edit the last decision
    // for other lifecycles, we want to edit the last non-plan f decision
    if (lifecycle !== ApplicationLifecycle.PLAN_F) {
      nonIgnoredDecisions = nonIgnoredDecisions.filter(
        d => d.decisionType !== DecisionType.PLAN_F,
      );
    }

    return nonIgnoredDecisions[nonIgnoredDecisions.length - 1];
  };

  const allDecisions = useSelector(state => state.decision.decisions);
  const engineMaxCoverage = allDecisions?.find(
    d => !d.ignored && d.decisionType === DecisionType.ENGINE,
  )?.coverage;

  function isLastDecisionAvailable() {
    const lastDecision = getLastDecision(decisions);
    if (lifecycle === ApplicationLifecycle.PLAN_F) {
      return lastDecision.decisionType === DecisionType.PLAN_F;
    }
    if (isActivated) {
      return (
        lastDecision.decisionType ===
        (isJuniorUw ? DecisionType.PRELIMINARY_PIA : DecisionType.PIA)
      );
    }

    return (
      lastDecision.decisionType ===
      (isJuniorUw ? DecisionType.PRELIMINARY_FINAL : DecisionType.FINAL)
    );
  }

  // Initialize the decision
  useEffect(() => {
    if (decisions.length) {
      if (isLastDecisionAvailable()) {
        const lastDecision = getLastDecision(decisions);
        setDecision(lastDecision?.decision);
      }
    }
  }, []);

  useEffect(() => {
    if (isOpen && decisions.length) {
      const lastDecision = getLastDecision(decisions);
      if (
        [
          DecisionType.FINAL,
          DecisionType.PIA,
          DecisionType.PRELIMINARY_FINAL,
          DecisionType.PRELIMINARY_PIA,
          DecisionType.PLAN_F,
        ].includes(lastDecision.decisionType)
      ) {
        setDecisionToEdit(lastDecision);
        setDecision(lastDecision?.decision);
      }
    }
  }, [isOpen]);

  return (
    <>
      {!areTasksResolved && (
        <InvalidDrawer
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          labelCopy={Resource.NO_DECISION_TO_EDIT}
        />
      )}
      {areTasksResolved && decisionToEdit && decision === Decision.APPROVED && (
        <ApproveDrawer
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          applicationId={id}
          hasFinalDecision={hasFinalDecision}
          hasPIADecision={hasPIADecision}
          editedDecision={decisionToEdit}
          setDecision={setDecision}
          updateReason={updateReason}
          setUpdateReason={setUpdateReason}
          engineMaxCoverage={engineMaxCoverage}
          isRUW={isRUW}
        />
      )}
      {areTasksResolved && decisionToEdit && decision === Decision.DECLINED && (
        <DeclineDrawer
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          applicationId={id}
          editedDecision={decisionToEdit}
          onDecisionChanged={setDecision}
        />
      )}
      {areTasksResolved &&
        decisionToEdit &&
        decision === Decision.INELIGIBLE && (
          <IneligibleDrawer
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            applicationId={id}
            editedDecision={decisionToEdit}
            onDecisionChanged={setDecision}
          />
        )}
      {areTasksResolved && decisionToEdit && decision === Decision.WAIVED && (
        <WaiveDrawer
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          applicationId={id}
          editedDecision={decisionToEdit}
          onDecisionChanged={setDecision}
        />
      )}
      {areTasksResolved &&
        decisionToEdit &&
        decision === Decision.POSTPONED && (
          <PostponeDrawer
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            applicationId={id}
            editedDecision={decisionToEdit}
            onDecisionChanged={setDecision}
          />
        )}
      {decision !== Decision.RESCINDED && isLastDecisionAvailable() && (
        <IconLink
          onClick={() => setIsOpen(true)}
          icon={faEdit}
          copy={Resource.EDIT_DECISION}
          iconContainerClassName={iconContainerClasses}
          iconClassName={styles.Icon}
        />
      )}
    </>
  );
};

export default EditDecision;
